import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import {
  ArrowRight, Target, Handshake, FlaskConical, Shield,
  BarChart3, Users, FileText, Microscope, Building2, Search,
  Zap, Globe, CheckCircle2, Database, Brain, ArrowUpRight, Layers,
  Radio, MapPin, Calendar, Plane, Activity, Globe2, Stethoscope
} from 'lucide-react';

/* ── Intersection Observer Hook ── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Animated Counter ── */
function Counter({ end, suffix = '', duration = 2000 }) {
  const [val, setVal] = useState(0);
  const [ref, inView] = useInView(0.3);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setVal(end); clearInterval(timer); }
      else setVal(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration]);
  return <span ref={ref}>{val.toLocaleString('en-US')}{suffix}</span>;
}

/* ═══════════════════════════════════════════════ */
/*  HERO                                           */
/* ═══════════════════════════════════════════════ */
function Hero() {
  return (
    <section className="relative min-h-screen flex items-center bg-[#0A1128] overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute inset-0 noise-overlay" />
      <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-[#00709B]/15 rounded-full blur-[120px] pulse-glow" />
      <div className="absolute bottom-1/4 -right-32 w-[400px] h-[400px] bg-[#70C24C]/10 rounded-full blur-[120px] pulse-glow" style={{ animationDelay: '1.5s' }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-32 lg:py-0 w-full">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#70C24C]/30 bg-[#70C24C]/5 text-[#70C24C] text-xs font-bold uppercase tracking-[0.2em] mb-8 reveal-up">
              <Radio className="w-3.5 h-3.5 animate-pulse" />
              Living Partnering Intelligence
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white tracking-tighter leading-[1.05] reveal-up stagger-1"
              data-testid="hero-heading"
            >
              We run the <span style={{color: '#70C24C'}}>partnering</span>
              <br />
              <span className="shimmer-text">engine</span>.
              <br />
              You get the deals.
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-[#A1A8C3] font-light leading-relaxed max-w-xl reveal-up stagger-2">
              Pharma BD teams subscribe to curated deal flow — never raw databases.
              Biotech founders engage success-only — no subscriptions, no platform.
              The intelligence layer is our moat. The outcomes are yours.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 reveal-up stagger-3">
              <Link
                to="/apply"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
                data-testid="hero-get-started-btn"
              >
                Start a Conversation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/platform"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/15 text-white text-sm font-medium hover:bg-white/5 hover:border-white/30 transition-all duration-300"
                data-testid="hero-platform-btn"
              >
                See How It Works
              </Link>
            </div>
          </div>

          {/* Right - Living Intelligence Visual */}
          <div className="lg:col-span-5 hidden lg:block">
            <div className="relative reveal-scale stagger-4">
              <div className="bg-[#111A3A] border border-white/5 rounded-3xl p-8 space-y-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#70C24C]">Live Intelligence Feed</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#70C24C] animate-pulse" />
                    <span className="text-[10px] text-[#70C24C] font-medium">LIVE</span>
                  </div>
                </div>
                {[
                  { label: 'Oncology mAb — Ph.II', match: '94%', source: 'BioChina 2026', color: '#70C24C', flag: 'CN' },
                  { label: 'Rare Neuro Gene Tx', match: '87%', source: 'BIO International', color: '#00709B', flag: 'US' },
                  { label: 'Immuno — ASO Platform', match: '82%', source: 'BIO-Europe', color: '#70C24C', flag: 'EU' },
                ].map((row, i) => (
                  <div key={i} className={`flex items-center justify-between p-4 rounded-xl bg-[#0A1128]/60 border border-white/5 reveal-up stagger-${i + 5}`}>
                    <div>
                      <p className="text-sm text-white font-medium">{row.label}</p>
                      <p className="text-[10px] text-[#687399] mt-0.5 flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5" /> {row.source}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: row.match, backgroundColor: row.color }} />
                      </div>
                      <span className="text-xs font-bold" style={{ color: row.color }}>{row.match}</span>
                    </div>
                  </div>
                ))}
                <div className="pt-2 flex items-center justify-between text-xs text-[#687399]">
                  <span className="flex items-center gap-1"><Activity className="w-3 h-3 text-[#70C24C]" /> Updated from 6 conferences</span>
                  <span className="text-[#00709B] font-medium flex items-center gap-1">Explore <ArrowUpRight className="w-3 h-3" /></span>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-2xl bg-gradient-to-br from-[#00709B] to-[#70C24C] opacity-20 blur-2xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  CONFERENCE INTELLIGENCE STRIP                  */
/* ═══════════════════════════════════════════════ */

// Master conference roster. Past entries auto-drop based on `endDate`.
// CPHI removed (not attending). Add new conferences here with ISO endDate
// and they will flow into both the strip and the bigger card section.
const CONFERENCES = [
  { name: 'BioChina 2026',                short: 'BioChina 2026',     locShort: 'Shanghai', locFull: 'Shanghai, China', dateLabel: 'Apr 28-29, 2026', endDate: '2026-04-29', assets: '180+ assets sourced' },
  { name: 'BIO International Convention', short: 'BIO International', locShort: 'San Diego', locFull: 'San Diego, USA',  dateLabel: 'Jun 2-5, 2026',   endDate: '2026-06-05', assets: '500+ meetings planned' },
  { name: 'BIO-Europe',                   short: 'BIO-Europe',        locShort: 'Stockholm', locFull: 'Stockholm, Sweden', dateLabel: 'Oct 2026',        endDate: '2026-10-31', assets: '300+ assets expected' },
  { name: 'BIO-Europe Spring',            short: 'BIO-Europe Spring', locShort: 'Europe',   locFull: 'Europe',           dateLabel: 'Mar 2027',        endDate: '2027-03-31', assets: 'Early pipeline focus' },
];

// Returns conferences whose end date is today or in the future, sorted earliest first.
// Tags the nearest as "Next Up" (green), next two as "Confirmed" (blue), the rest "Planned" (grey).
function getUpcomingConferences(limit = null) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcoming = CONFERENCES
    .filter(c => new Date(c.endDate) >= today)
    .sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
  const palette = ['#70C24C', '#00709B', '#00709B', '#687399', '#687399'];
  const statuses = ['Next Up', 'Confirmed', 'Confirmed', 'Planned', 'Planned'];
  const out = upcoming.map((c, i) => ({
    ...c,
    status: statuses[i] || 'Planned',
    color: palette[i] || '#687399',
  }));
  return limit ? out.slice(0, limit) : out;
}

function ConferenceStrip() {
  const conferences = getUpcomingConferences();
  if (conferences.length === 0) return null;

  return (
    <section className="relative bg-[#111A3A] border-y border-white/5 overflow-hidden" data-testid="conference-strip">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
        <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-2 shrink-0">
            <Plane className="w-4 h-4 text-[#70C24C]" />
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#70C24C] whitespace-nowrap">On the Ground</span>
          </div>
          <div className="w-px h-8 bg-white/10 shrink-0" />
          {conferences.map((c, i) => (
            <div key={c.name} className="flex items-center gap-3 shrink-0 group">
              <div className="flex items-center gap-2">
                {c.status === 'Next Up' && <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: c.color }} />}
                {c.status === 'Confirmed' && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />}
                {c.status === 'Planned' && <div className="w-1.5 h-1.5 rounded-full bg-[#687399]" />}
                <span className="text-sm text-white font-medium whitespace-nowrap">{c.short}</span>
              </div>
              <span className="text-[10px] text-[#687399] whitespace-nowrap">{c.locShort} | {c.dateLabel.replace(', 2026','').replace(', 2027','')}</span>
              {i < conferences.length - 1 && <div className="w-px h-4 bg-white/10 ml-3" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  METRICS BAR                                    */
/* ═══════════════════════════════════════════════ */
function Metrics() {
  const [ref, inView] = useInView(0.3);
  const items = [
    { value: 14000, suffix: '+', label: 'Biotech Assets Indexed' },
    { value: 2800, suffix: '+', label: 'BD Contacts Mapped' },
    { value: 4250, suffix: '+', label: 'Biotech Companies' },
    { value: 20, suffix: '+', label: 'Commercial Partners' },
  ];
  return (
    <section ref={ref} className="relative bg-[#0A1128]" data-testid="metrics-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {items.map((m, i) => (
            <div key={i} className={`text-center lg:text-left ${inView ? 'reveal-up stagger-' + (i + 1) : 'opacity-0'}`} data-testid={`metric-${i}`}>
              <div className="text-3xl lg:text-4xl font-black text-white tracking-tight">
                <Counter end={m.value} suffix={m.suffix} />
              </div>
              <p className="mt-1 text-xs lg:text-sm text-[#687399] font-medium">{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  THE LIVING ENGINE                              */
/* ═══════════════════════════════════════════════ */
function LivingEngine() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A1128] relative" data-testid="living-engine-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-16 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Why "Living"?</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Databases are dead. This is alive.
          </h2>
          <p className="mt-5 text-lg text-[#A1A8C3] font-light max-w-3xl mx-auto">
            Every other partnering platform gives you a snapshot. A directory frozen in time.
            ForesightRx is continuously updated from the ground — every conference, every meeting,
            every qualified conversation feeds the intelligence engine. In real time.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {[
            {
              icon: <Plane className="w-6 h-6" />,
              title: 'Boots on the Ground',
              desc: 'We attend 12+ global conferences annually. We meet the founders. We see the data rooms. We assess the science. No other platform does this.',
              accent: '#70C24C',
            },
            {
              icon: <Brain className="w-6 h-6" />,
              title: 'AI-Powered Matching',
              desc: 'Every qualified biotech asset is indexed by therapeutic area, stage, modality, geography, and deal readiness. Our AI maps it against pharma BD theses automatically.',
              accent: '#00709B',
            },
            {
              icon: <Handshake className="w-6 h-6" />,
              title: 'Human-Facilitated Deals',
              desc: 'We don\'t just match — we introduce, moderate, and facilitate. CDAs, NCD exchange, meeting prep, follow-ups. We stay in the loop until the deal is done.',
              accent: '#70C24C',
            },
          ].map((c, i) => (
            <div key={i} className={`bg-[#111A3A] border border-white/5 rounded-2xl p-8 hover:border-white/10 transition-all duration-500 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: `${c.accent}15`, color: c.accent }}>
                {c.icon}
              </div>
              <h4 className="text-lg font-bold text-white mb-3">{c.title}</h4>
              <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        <div className={`mt-12 text-center ${inView ? 'reveal-up stagger-4' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#111A3A] border border-white/5">
            <div className="w-2 h-2 rounded-full bg-[#70C24C] animate-pulse" />
            <span className="text-sm text-[#A1A8C3]">Intelligence updated continuously from <span className="text-white font-semibold">12+ global conferences</span> per year</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  THE PROBLEM (Pain Point)                       */
/* ═══════════════════════════════════════════════ */
function TheProblem() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#111A3A] relative" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`max-w-3xl mb-16 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">The Problem</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Pharma deal-making is broken.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {[
            { word: 'Fragmented', pain: 'Assets scattered across 10,000+ biotechs in 45+ markets. No single layer connects them to pharma capital.', icon: <Database className="w-5 h-5" /> },
            { word: 'Inefficient', pain: 'Fly to 12 conferences. Sit through 40 meetings. Walk away with 3 leads. $615K+ annual BD cost — most of it wasted.', icon: <Building2 className="w-5 h-5" /> },
            { word: 'Relationship-Driven', pain: 'Deals still happen through who-you-know. Great science dies unfunded because the right buyer never sees it.', icon: <FlaskConical className="w-5 h-5" /> },
            { word: 'Low Conversion', pain: '12-18 month deal cycles. Static databases, cold outreach, slow diligence. Both sides lose time and money.', icon: <Shield className="w-5 h-5" /> },
          ].map((p, i) => (
            <div key={i} className={`bg-[#0A1128]/60 border border-white/5 rounded-2xl p-6 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}>
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center text-red-400 mb-4">
                {p.icon}
              </div>
              <h4 className="text-lg font-bold text-white mb-2">{p.word}</h4>
              <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{p.pain}</p>
            </div>
          ))}
        </div>

        <div className={`bg-gradient-to-r from-[#70C24C]/8 to-[#00709B]/8 border border-[#70C24C]/20 rounded-2xl p-6 text-center ${inView ? 'reveal-up stagger-5' : 'opacity-0'}`}>
          <p className="text-xl text-white font-bold">We are fixing this.</p>
          <p className="text-sm text-[#A1A8C3] font-light mt-1">
            Building the intelligence infrastructure that connects biotech innovation to pharma capital — globally, intelligently, and fast.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  THREE AUDIENCES                                */
/* ═══════════════════════════════════════════════ */
function DualAudience() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A1128] relative" data-testid="dual-audience-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-16 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Two Audiences. One Engine.</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Built for the people who move the deal.
          </h2>
          <p className="mt-5 text-base lg:text-lg text-[#A1A8C3] font-light max-w-3xl mx-auto">
            Pharma BD subscribes to curated intelligence. Biotech founders engage success-only.
            Neither of them logs into our system — that&apos;s the point.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-7 max-w-4xl mx-auto">
          {/* PHARMA */}
          <div className={`group bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-3xl p-7 lg:p-9 hover:border-[#00709B]/40 transition-all duration-500 ${inView ? 'reveal-up stagger-1' : 'opacity-0'}`} data-testid="card-pharma">
            <div className="w-12 h-12 rounded-2xl bg-[#00709B]/15 flex items-center justify-center mb-6 group-hover:bg-[#00709B]/25 transition-colors duration-300">
              <Building2 className="w-6 h-6 text-[#00709B]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Pharma BD</h3>
            <p className="text-xs uppercase tracking-wider text-[#00709B] font-bold mb-4">Subscribe to outcomes</p>
            <p className="text-[#A1A8C3] font-light leading-relaxed mb-6 text-sm">
              Curated, continuously updated deal flow aligned to your BD thesis.
              You receive prioritized assets, deal benchmarks, and warm introductions —
              never raw data, never platform access.
            </p>
            <div className="space-y-2.5 mb-7">
              {[
                'Therapeutic-area scouting feeds (China-origin, India, APAC, EU, US)',
                'Deal benchmarking + competitive partnering signals',
                'Pre-qualified asset shortlists with our human judgment layer',
                'Curated intros — biotechs never see your BD thesis',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 w-4 h-4 text-[#00709B] shrink-0" />
                  <span className="text-xs text-[#A1A8C3]">{item}</span>
                </div>
              ))}
            </div>
            <Link to="/platform" className="inline-flex items-center gap-2 text-sm font-semibold text-[#00709B] hover:text-white transition-colors duration-300 group/link" data-testid="pharma-link">
              How pharma engages <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          {/* BIOTECH */}
          <div className={`group bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-3xl p-7 lg:p-9 hover:border-[#70C24C]/30 transition-all duration-500 ${inView ? 'reveal-up stagger-2' : 'opacity-0'}`} data-testid="card-biotech">
            <div className="w-12 h-12 rounded-2xl bg-[#70C24C]/10 flex items-center justify-center mb-6 group-hover:bg-[#70C24C]/20 transition-colors duration-300">
              <FlaskConical className="w-6 h-6 text-[#70C24C]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Biotech Innovators</h3>
            <p className="text-xs uppercase tracking-wider text-[#70C24C] font-bold mb-4">Pay only on success</p>
            <p className="text-[#A1A8C3] font-light leading-relaxed mb-6 text-sm">
              Get your asset in front of the right pharma buyers without expensive conference travel
              or giving away your data. Zero subscription. We earn only when your deal closes.
            </p>
            <div className="space-y-2.5 mb-7">
              {[
                'Positioned to pre-qualified pharma — never blasted',
                'Confidential data stays with us until you approve each intro',
                'NCD orchestration, CDA management, deal facilitation',
                'No upfront cost — we invest first, share success on close',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 w-4 h-4 text-[#70C24C] shrink-0" />
                  <span className="text-xs text-[#A1A8C3]">{item}</span>
                </div>
              ))}
            </div>
            <Link to="/partnering" className="inline-flex items-center gap-2 text-sm font-semibold text-[#70C24C] hover:text-white transition-colors duration-300 group/link" data-testid="biotech-link">
              How partnering works <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  CONFERENCE INTELLIGENCE                        */
/* ═══════════════════════════════════════════════ */
function ConferenceIntelligence() {
  const [ref, inView] = useInView();
  // Show next 3 upcoming conferences only. CPHI removed at source.
  const conferences = getUpcomingConferences(3).map(c => ({
    name: c.name,
    location: c.locFull,
    date: c.dateLabel,
    status: c.status,
    color: c.color,
    assets: c.assets,
  }));

  if (conferences.length === 0) return null;

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#111A3A] relative" data-testid="conference-intel-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Conference Intelligence — Next 3 Upcoming</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Our data sources have handshakes.
            </h2>
            <p className="mt-4 text-lg text-[#A1A8C3] font-light max-w-2xl">
              While others scrape press releases, we are on the ground. Every conference
              we attend feeds fresh, qualified intelligence into the platform.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {conferences.map((c, i) => (
            <div key={c.name} className={`bg-[#0A1128]/60 border border-white/5 rounded-2xl p-6 hover:border-white/15 transition-all duration-300 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`conf-card-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${c.color}15`, color: c.color }}>
                  {c.status}
                </span>
                <Calendar className="w-4 h-4 text-[#687399]" />
              </div>
              <h4 className="text-base font-bold text-white mb-1">{c.name}</h4>
              <p className="text-xs text-[#687399] flex items-center gap-1 mb-3">
                <MapPin className="w-3 h-3" /> {c.location} | {c.date}
              </p>
              <p className="text-xs text-[#A1A8C3] font-light">{c.assets}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  PLATFORM INTELLIGENCE (BENTO GRID)             */
/* ═══════════════════════════════════════════════ */
function PlatformIntel() {
  const [ref, inView] = useInView();
  const cards = [
    {
      span: 'lg:col-span-7',
      icon: <Brain className="w-6 h-6" />,
      title: 'AI-Powered Asset Matching',
      desc: 'Our intelligence engine maps biotech assets against pharma BD theses using therapeutic area, clinical stage, modality, and geography — continuously updated from every conference we attend.',
      visual: (
        <div className="mt-6 grid grid-cols-3 gap-3">
          {['Oncology','Neurology','Immunology','Rare Disease','Cardiology','Metabolic'].map((ta, i) => (
            <div key={i} className="px-3 py-2 rounded-lg bg-white/5 border border-white/5 text-center">
              <p className="text-[10px] text-[#687399] mb-1">{ta}</p>
              <p className="text-sm font-bold text-white">{[312,184,147,96,73,68][i]}</p>
            </div>
          ))}
        </div>
      ),
    },
    { span: 'lg:col-span-5', icon: <Globe className="w-6 h-6" />, title: 'Cross-Border Deal Facilitation', desc: 'Deep networks across China, India, APAC, and global pharma. CDA orchestration, NCD exchange, warm introductions — end-to-end from first signal to signed deal.' },
  ];

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A1128] relative" data-testid="platform-intel-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-20 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-5">The Intelligence Layer</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Data + judgment + execution
          </h2>
          <p className="mt-6 text-lg text-[#A1A8C3] font-light max-w-2xl mx-auto leading-relaxed">
            Not another database. A living deal enablement engine that combines AI-powered intelligence with human expertise from the world's top partnering conferences.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {cards.map((c, i) => (
            <div key={i} className={`${c.span} bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-2xl p-8 lg:p-10 hover:border-[#00709B]/30 transition-all duration-500 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`intel-card-${i}`}>
              <div className="w-12 h-12 rounded-2xl bg-[#00709B]/15 flex items-center justify-center text-[#00709B] mb-5">{c.icon}</div>
              <h4 className="text-lg lg:text-xl font-bold text-white mb-3">{c.title}</h4>
              <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{c.desc}</p>
              {c.visual}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  HOW IT WORKS                                   */
/* ═══════════════════════════════════════════════ */
function HowItWorks() {
  const [ref, inView] = useInView();
  const steps = [
    { num: '01', title: 'Tell us what you need', desc: 'Share your BD thesis, therapeutic focus, and deal goals.' },
    { num: '02', title: 'We scout on the ground', desc: 'At conferences worldwide, we meet founders, assess science, and qualify assets.' },
    { num: '03', title: 'AI matches + human curation', desc: 'Our engine surfaces the best fits. Our team adds context and judgment.' },
    { num: '04', title: 'Managed introductions', desc: 'We position, reach out, facilitate NCD exchange, and manage CDAs.' },
    { num: '05', title: 'Deal to close', desc: 'Meeting prep, due diligence support, and facilitation until the deal is done.' },
  ];

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#111A3A] relative" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-16 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">The Deal Pathway</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            From first signal to signed deal
          </h2>
        </div>
        <div className="relative">
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="grid sm:grid-cols-5 gap-8 lg:gap-6">
            {steps.map((s, i) => (
              <div key={i} className={`relative text-center ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`step-${i}`}>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#70C24C]/10 border border-[#70C24C]/20 text-[#70C24C] text-sm font-black mb-4">{s.num}</div>
                <h4 className="text-sm font-bold text-white mb-2">{s.title}</h4>
                <p className="text-xs text-[#687399] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  WHY US                                         */
/* ═══════════════════════════════════════════════ */
function WhyUs() {
  const [ref, inView] = useInView();
  const reasons = [
    { icon: <Shield className="w-5 h-5" />, title: 'The intelligence layer is the moat — not the product', desc: '4,250+ biotechs, 14,000+ assets, 2,800+ pharma contacts, 3 years of pharma reply data. Pharma can\'t self-serve this; building it requires multi-year relationships and conference presence.' },
    { icon: <Plane className="w-5 h-5" />, title: 'We Show Up', desc: '12+ conferences a year. We meet the founders, assess the science, and build relationships others can\'t replicate from a desk.' },
    { icon: <Target className="w-5 h-5" />, title: 'Judgment, Not Just Data', desc: 'Every recommendation is human-qualified. AI augmented, expert-directed. Curated insight, not raw data.' },
    { icon: <Globe className="w-5 h-5" />, title: 'Cross-Border Reach', desc: 'Deep networks across China, India, APAC, and global pharma. Regional context and trusted access.' },
    { icon: <Activity className="w-5 h-5" />, title: 'Living Intelligence', desc: 'Our engine updates after every conference, every meeting, every reply. Static databases can\'t compete.' },
    { icon: <Zap className="w-5 h-5" />, title: 'Speed to Market', desc: 'Every month saved in licensing is a month of patent-protected revenue. Our process compresses quarters into weeks.' },
  ];

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A1128]" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-16 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Why ForesightRx</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Platforms give you access. We give you outcomes.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {reasons.map((r, i) => (
            <div key={i} className={`bg-[#111A3A] border border-white/5 rounded-2xl p-6 hover:border-white/10 transition-all duration-300 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`why-card-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-[#00709B]/15 flex items-center justify-center text-[#00709B] mb-5">{r.icon}</div>
              <h4 className="text-base font-bold text-white mb-2">{r.title}</h4>
              <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  TWO TRACKS — dual audience entry strip         */
/* ═══════════════════════════════════════════════ */
function TwoTracks() {
  const [ref, inView] = useInView();
  const tracks = [
    {
      to: '/for-pharma',
      audience: 'For Pharma BD',
      headline: 'Cross-border deal flow, fully de-risked.',
      desc: 'JCA-aware shortlists. Conference BD. 12-month retainers calibrated to your geography.',
      icon: <Building2 className="w-6 h-6" />,
      accent: '#00709B',
      cta: 'Pharma engagement',
      testid: 'two-tracks-pharma',
    },
    {
      to: '/for-biotech',
      audience: 'For Emerging Biotech',
      headline: 'Look larger than life to the partners that matter.',
      desc: 'Pharma-grade positioning + EU JCA + US AMCP readiness. Success-only fee.',
      icon: <FlaskConical className="w-6 h-6" />,
      accent: '#70C24C',
      cta: 'Biotech engagement',
      testid: 'two-tracks-biotech',
    },
  ];
  return (
    <section ref={ref} className="py-20 lg:py-24 bg-[#0A1128] relative" data-testid="two-tracks-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-12 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-3">Pick your track</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Two audiences. One engine. Sharper outcomes.
          </h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-7">
          {tracks.map((t, i) => (
            <Link
              key={t.to}
              to={t.to}
              className={`group relative overflow-hidden bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-3xl p-8 lg:p-10 hover:border-white/20 hover:-translate-y-1 transition-all duration-500 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}
              data-testid={t.testid}
            >
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-[60px] opacity-30" style={{ backgroundColor: t.accent }} />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-5" style={{ backgroundColor: `${t.accent}15`, color: t.accent }}>
                  {t.audience}
                </div>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${t.accent}15`, color: t.accent }}>
                  {t.icon}
                </div>
                <h3 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight leading-tight mb-3">
                  {t.headline}
                </h3>
                <p className="text-base text-[#A1A8C3] font-light leading-relaxed mb-7 max-w-md">{t.desc}</p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold group-hover:translate-x-1 transition-transform duration-300" style={{ color: t.accent }}>
                  {t.cta} <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  THREE PILLARS — JCA / AMCP differentiation     */
/* ═══════════════════════════════════════════════ */
function ThreePillars() {
  const [ref, inView] = useInView();
  const pillars = [
    {
      icon: <Handshake className="w-6 h-6" />,
      accent: '#70C24C',
      tag: 'Partnering',
      title: 'Partnering Intelligence',
      desc: 'AI-scored outreach to the pharma buyers most likely to care. 2,800+ mapped BD contacts. Buyer-specific shortlists tuned to each pharma in-licensor\'s deck.',
    },
    {
      icon: <Globe2 className="w-6 h-6" />,
      accent: '#00709B',
      tag: 'EU',
      title: 'EU JCA / HTA Readiness',
      desc: 'Joint Clinical Assessment in scope since January 2025 for oncology & ATMPs. PICO mapping, comparator landscape, ITC feasibility, evidence-gap analysis.',
    },
    {
      icon: <Stethoscope className="w-6 h-6" />,
      accent: '#A88BFF',
      tag: 'US',
      title: 'US AMCP / Payer-Value Readiness',
      desc: 'AMCP-format dossier, ICER preparedness, RWE strategy, budget-impact modeling, payer objection mapping. Pharma-grade rigour from the MarksMan team.',
    },
  ];
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0E1733] relative" data-testid="three-pillars-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`max-w-3xl mb-16 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">The Three Pillars</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Partnering. Plus the payer story — in both major markets.
          </h2>
          <p className="mt-5 text-lg text-[#A1A8C3] font-light">
            Partnering intelligence is necessary. It is not sufficient. EU JCA and US AMCP / payer-value
            readiness decide whether a licensing deal becomes a commercial success — and we work both lenses
            into every engagement.
          </p>
        </div>
        <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
          {pillars.map((p, i) => (
            <div
              key={i}
              className={`bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-3xl p-7 lg:p-8 hover:border-white/15 transition-all duration-500 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}
              data-testid={`home-pillar-${i}`}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${p.accent}15`, color: p.accent }}>
                  {p.icon}
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${p.accent}15`, color: p.accent }}>
                  {p.tag}
                </span>
              </div>
              <h3 className="text-lg lg:text-xl font-bold text-white mb-3">{p.title}</h3>
              <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
        <div className={`mt-10 text-center ${inView ? 'reveal-up stagger-4' : 'opacity-0'}`}>
          <Link to="/for-biotech" className="inline-flex items-center gap-2 text-sm font-semibold text-[#70C24C] hover:text-white transition-colors duration-300" data-testid="home-pillars-cta">
            See how the Three Pillars work for emerging biotech <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  PROOF IN PRACTICE — case study teaser          */
/* ═══════════════════════════════════════════════ */
function ProofInPractice() {
  const [ref, inView] = useInView();
  const cases = [
    {
      to: '/case-studies#eu-specialty',
      tag: 'Pharma BD',
      tagColor: '#00709B',
      client: 'Confidential · EU Specialty Pharma',
      headline: 'EU commercialization-ready shortlist',
      blurb: 'Deck-aligned deterministic scoring + per-asset web enrichment for a 25+ market specialty BD team.',
      testid: 'proof-eu-specialty',
    },
    {
      to: '/case-studies#cross-border',
      tag: 'Cross-border',
      tagColor: '#70C24C',
      client: 'Confidential · US Specialty Pharma',
      headline: 'China-origin pipeline into Western pharma',
      blurb: 'Living shortlist sourced from NMPA, ChinaBio floor coverage, and direct BD network in Shanghai · Beijing · Suzhou.',
      testid: 'proof-cross-border',
    },
    {
      to: '/case-studies#biotech-anon',
      tag: 'Emerging Biotech',
      tagColor: '#A88BFF',
      client: 'Confidential · Oncology',
      headline: 'From "no traction" to first term sheet',
      blurb: 'Out-licensing positioning + EU JCA pre-read + US AMCP outline + curated pharma BD intros — success-only fee.',
      testid: 'proof-biotech',
    },
  ];
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A1128] relative" data-testid="proof-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Proof in practice</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Engagements that already shipped.
            </h2>
          </div>
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-semibold text-[#70C24C] hover:text-white transition-colors shrink-0" data-testid="proof-all-link">
            All case studies <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid lg:grid-cols-3 gap-5">
          {cases.map((c, i) => (
            <Link
              key={c.to}
              to={c.to}
              className={`group bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-2xl p-7 hover:border-white/20 hover:-translate-y-1 transition-all duration-500 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}
              data-testid={c.testid}
            >
              <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-5" style={{ backgroundColor: `${c.tagColor}15`, color: c.tagColor }}>
                {c.tag}
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#687399] mb-2">{c.client}</p>
              <h4 className="text-lg font-bold text-white mb-3 leading-tight">{c.headline}</h4>
              <p className="text-sm text-[#A1A8C3] font-light leading-relaxed mb-5">{c.blurb}</p>
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#70C24C] group-hover:translate-x-1 transition-transform">
                Read case study <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ═══════════════════════════════════════════════ */
/*  CTA                                            */
/* ═══════════════════════════════════════════════ */
function CTA() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#111A3A] relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#00709B]/10 to-[#70C24C]/10 blur-[100px]" />
      </div>
      <div className={`relative max-w-3xl mx-auto px-6 lg:px-12 text-center ${inView ? 'reveal-up' : 'opacity-0'}`}>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6">
          The next deal is already in our pipeline.
        </h2>
        <p className="text-lg text-[#A1A8C3] font-light leading-relaxed mb-10 max-w-xl mx-auto">
          Pharma BD looking for curated deal flow. Biotech with a partnerable asset. Tell us which one you are.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/apply" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-base font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]" data-testid="bottom-cta-btn">
            Start a Conversation <ArrowRight className="w-5 h-5" />
          </Link>
          <Link to="/platform" className="inline-flex items-center gap-2 px-10 py-4 rounded-full border border-white/15 text-white text-base font-medium hover:bg-white/5 hover:border-white/30 transition-all duration-300" data-testid="bottom-services-btn">
            See How It Works
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  PAGE                                           */
/* ═══════════════════════════════════════════════ */
export default function HomePage() {
  useSEO({
    title: 'ForesightRx — Decision Intelligence for Pharma BD',
    description: 'We run the partnering engine; you get the deals. Pharma BD subscribes to curated cross-border deal flow. Biotech founders engage success-only. Conference-sourced intelligence across 12+ industry events.',
    canonical: 'https://foresightrx.com/',
  });
  usePageTracker('Home');

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect">
      <PublicNav />
      <Hero />
      <ConferenceStrip />
      <TwoTracks />
      <Metrics />
      <LivingEngine />
      <ThreePillars />
      <TheProblem />
      <DualAudience />
      <ConferenceIntelligence />
      <PlatformIntel />
      <HowItWorks />
      <WhyUs />
      <ProofInPractice />
      <CTA />
      <PublicFooter />
    </div>
  );
}
